import { translate } from "@/config/translator";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from "@fluentui/react-components";
import ChangePasswordForm from "./changePasswordForm";
import { useChangePasswordDialog } from "./changePasswordDialog/useChangePasswordDialog";
import { Dispatch, SetStateAction } from "react";
import { PasswordPolicyError } from "@/config/types";
import { FullScreenSpinner } from "@/components/fullScreenSpinner/FullScreenSpinner";

export declare type ChangePasswordDialogType = {
  isOpen: boolean;
  onClose: () => void;
  passwordRequirements: Record<PasswordPolicyError, boolean>;
  setPasswordRequirements: Dispatch<
    SetStateAction<Record<PasswordPolicyError, boolean>>
  >;
  onSaveNewPassword: (
    oldPassword: string,
    newPassword: string,
  ) => Promise<void>;
  wrongPassword: boolean;
  setWrongPassword: (value: boolean) => void;
  changePasswordButtonDisabled: boolean;
};

export default function ChangePasswordDialog({
  isOpen,
  onSaveNewPassword,
  onClose,
  wrongPassword,
  setWrongPassword,
  changePasswordButtonDisabled,
  passwordRequirements,
  setPasswordRequirements,
}: ChangePasswordDialogType) {
  const {
    onCloseWrapper,
    onSaveNewPasswordWrapper,
    passwordsDontMatch,
    passwordIsEmpty,
    oldPassword,
    setOldPassword,
    newPassword,
    setNewPassword,
    confirmNewPassword,
    setConfirmNewPassword,
    lang,
    passwordPolicy,
    passwordInvalid,
  } = useChangePasswordDialog({
    onClose,
    onSaveNewPassword,
    passwordRequirements,
    setPasswordRequirements,
  });

  return (
    <Dialog open={isOpen}>
      <DialogSurface>
        <DialogBody>
          {/* TITLE */}
          <DialogTitle>{translate("changePassword", lang)}</DialogTitle>
          <DialogContent>
            {passwordPolicy ? (
              <ChangePasswordForm
                passwordPolicy={passwordPolicy}
                passwordRequirements={passwordRequirements}
                oldPassword={oldPassword}
                newPassword={newPassword}
                confirmNewPassword={confirmNewPassword}
                passwordsDontMatch={passwordsDontMatch}
                setOldPassword={setOldPassword}
                setNewPassword={setNewPassword}
                setConfirmNewPassword={setConfirmNewPassword}
                setWrongPassword={setWrongPassword}
                wrongPassword={wrongPassword}
                setPasswordRequirements={setPasswordRequirements}
              />
            ) : (
              <FullScreenSpinner />
            )}
          </DialogContent>
          <DialogActions>
            {/* CANCEL */}
            <DialogTrigger>
              <Button onClick={onCloseWrapper}>
                {translate("cancel", lang)}
              </Button>
            </DialogTrigger>

            {/* SAVE */}
            <Button
              appearance="primary"
              disabled={
                passwordsDontMatch ||
                passwordIsEmpty ||
                oldPassword.length === 0 ||
                changePasswordButtonDisabled ||
                passwordInvalid
              }
              onClick={onSaveNewPasswordWrapper}
            >
              {translate("changePassword", lang)}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
